/**
 * Recording Coach — the single guidance toast shown while an adult narrates.
 *
 * One fixed-position overlay anchored above the audio controls. It must never
 * sit in the flex flow of .story-reader-container, because that pushes the
 * player up and squeezes the book (the bug reported on v0.35.38).
 *
 * Messages carry icon tokens so the copy stays translator-friendly and shows
 * the real buttons rather than an emoji:
 *   "Druk die {record} om te begin opneem"
 *
 * Related: #146
 */

import { renderTokenisedMessage } from './toast.js';

let coachEl = null;
let dismissTimer = null;
let repositionBound = false;

/**
 * Build message content, swapping icon tokens for images.
 *
 * The token map and renderer live in toast.js so both surfaces that show this
 * copy stay in step — an icon path updated in one place would otherwise go
 * stale in the other. The coach keeps its own icon class so it can size the
 * images to its own typography.
 *
 * @param {string} text
 * @returns {DocumentFragment}
 */
function buildMessage(text) {
    return renderTokenisedMessage(text, 'recording-coach__icon');
}

/**
 * Position the coach vertically centred in the free space between the bottom
 * of the book and the top of the audio controls.
 *
 * This gap varies by device: it is substantial on taller phones (iPhone 12 Pro)
 * and nearly zero on short ones (iPhone SE). The toast should never cover any
 * part of the reader page or the controls — it sits exactly in the middle of
 * whatever space is available.
 *
 * Falls back to a small offset from the bottom when the controls or book are
 * not on screen.
 */
function reposition() {
    if (!coachEl) return;

    const controls = document.getElementById('audioControls');
    const flipBook = document.getElementById('flipBook');
    const visible = controls && controls.classList.contains('audio-controls--visible');
    const gap = 12;

    if (visible && flipBook) {
        const bookRect = flipBook.getBoundingClientRect();
        const controlsRect = controls.getBoundingClientRect();

        // The free space runs from the book's bottom to the controls' top
        const freeTop = bookRect.bottom;
        const freeBottom = controlsRect.top;
        const freeHeight = freeBottom - freeTop;

        if (freeHeight > 0) {
            const height = coachEl.offsetHeight || 0;

            // Centre in the gap. If the toast is taller than the gap, pin its
            // bottom to the controls' top so it overlaps the book's page-number
            // footer rather than the control buttons (which need to stay tappable).
            let top;
            if (height <= freeHeight) {
                // Fits perfectly — true centre
                top = freeTop + (freeHeight - height) / 2;
            } else {
                // Toast is taller than the gap — pin bottom to controls, nudged
                // down slightly on very tight screens so it sits over the
                // page-number footer rather than covering story text.
                top = freeBottom - height + 6;
            }

            coachEl.style.bottom = '';
            coachEl.style.top = `${Math.max(top, 0)}px`;
            return;
        }
    }

    // Fallback: just above the controls (or bottom of screen)
    if (visible) {
        const rect = controls.getBoundingClientRect();
        const seamFromBottom = window.innerHeight - rect.top;
        const height = coachEl.offsetHeight || 0;
        coachEl.style.top = '';
        coachEl.style.bottom = `${Math.max(seamFromBottom - height / 2, gap)}px`;
    } else {
        coachEl.style.top = '';
        coachEl.style.bottom = `${gap}px`;
    }
}

/**
 * Create the coach element on first use.
 * @returns {HTMLElement|null}
 */
function ensureCoach() {
    if (coachEl && document.body.contains(coachEl)) return coachEl;

    coachEl = document.createElement('div');
    coachEl.id = 'recordingCoach';
    coachEl.className = 'recording-coach';
    coachEl.setAttribute('role', 'status');
    coachEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(coachEl);

    if (!repositionBound) {
        window.addEventListener('resize', reposition);
        window.addEventListener('orientationchange', reposition);
        repositionBound = true;
    }

    return coachEl;
}

/**
 * Show or update the coach.
 *
 * @param {Object} options
 * @param {string} options.message - Copy, may contain icon tokens
 * @param {string|null} [options.badge] - Short position text ("2/7") for the corner circle
 * @param {string|null} [options.badgeTitle] - Spelled-out version for screen readers
 * @param {string} [options.tone] - 'info' | 'success' | 'error'
 * @param {number|null} [options.autoDismissMs] - Auto-hide delay; null keeps it up
 * @param {Object|null} [options.action] - { label, onClick, disabled }
 * @returns {void}
 */
export function showCoach({
    message,
    badge = null,
    badgeTitle = null,
    tone = 'info',
    autoDismissMs = null,
    action = null,
} = {}) {
    const el = ensureCoach();
    if (!el) return;

    clearTimeout(dismissTimer);
    dismissTimer = null;

    el.className = `recording-coach recording-coach--${tone}`;
    el.textContent = '';

    const messageEl = document.createElement('div');
    messageEl.className = 'recording-coach__message';
    messageEl.appendChild(buildMessage(message));
    el.appendChild(messageEl);

    // Position rides in a circle on the toast's corner rather than a second
    // line of text, which made the toast tall enough to cover the artwork.
    if (badge) {
        const badgeEl = document.createElement('span');
        badgeEl.className = 'recording-coach__badge';
        badgeEl.textContent = badge;
        if (badgeTitle) {
            badgeEl.title = badgeTitle;
            badgeEl.setAttribute('aria-label', badgeTitle);
        }
        el.appendChild(badgeEl);
    }

    if (action && action.label) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'recording-coach__action';
        btn.textContent = action.label;
        btn.disabled = !!action.disabled;
        if (typeof action.onClick === 'function') {
            btn.addEventListener('click', action.onClick);
        }
        el.appendChild(btn);
    }

    reposition();
    requestAnimationFrame(() => {
        el.classList.add('is-visible');
        // Re-measure after the element has been laid out — offsetHeight is 0
        // before the first paint, which makes the centring calculation wrong.
        reposition();
    });

    if (autoDismissMs) {
        dismissTimer = setTimeout(() => hideCoach(), autoDismissMs);
    }
}

/**
 * Hide the coach without destroying it.
 * @returns {void}
 */
export function hideCoach() {
    clearTimeout(dismissTimer);
    dismissTimer = null;
    if (coachEl) coachEl.classList.remove('is-visible');
}

/**
 * Remove the coach entirely. Called when leaving the reader.
 * @returns {void}
 */
export function destroyCoach() {
    clearTimeout(dismissTimer);
    dismissTimer = null;
    if (coachEl) {
        coachEl.remove();
        coachEl = null;
    }
}

/**
 * Whether the coach is currently on screen.
 * @returns {boolean}
 */
export function isCoachVisible() {
    return !!coachEl && coachEl.classList.contains('is-visible');
}
